import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	fetchContactStaff,
	createContactStaff,
	updateContactStaff,
	deleteContactStaff,
} from "../../api/controller/admin/adminContactStaff";
import AdminContactStaffDialogBox from "./adminDialogBoxes/AdminContactStaffDialogBox";
import AdminConfirmDialog from "./adminDialogBoxes/AdminConfirmDialog";
import { RegionsCardData } from "../../api/controller/videoController/regions";
import { useFlashMessage } from "../../components/FlashMessageProvider";

const AdminContactStaff = () => {
	const navigate = useNavigate();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogInitial, setDialogInitial] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
	const { showMessage } = useFlashMessage();
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [regionsLookup, setRegionsLookup] = useState({});

	useEffect(() => {
		let mounted = true;
		const load = async () => {
			setLoading(true);
			setError(null);
			try {
				const res = await fetchContactStaff();
				if (mounted) setItems(res || []);
			} catch (err) {
				console.error(err);
				if (mounted) setError("Failed to load contact staff items.");
			} finally {
				if (mounted) setLoading(false);
			}
		};
		load();
		return () => { mounted = false; };
	}, []);

	useEffect(() => {
		let mounted = true;
		const loadRegions = async () => {
			try {
				const data = await RegionsCardData();
				if (!mounted) return;
				const map = {};
				(Array.isArray(data) ? data : []).forEach((region) => {
					if (!region || !(region._id || region.id)) return;
					const id = region._id || region.id;
					const city = region.city || '';
					const state = region.state || '';
					const label = city || state || id;
					map[id] = label;
				});
				setRegionsLookup(map);
			} catch (err) {
				console.error('Failed to load regions list for contact staff.', err);
			}
		};
		loadRegions();
		return () => { mounted = false; };
	}, []);

	const userType = localStorage.getItem("userType");
	const isAdmin = userType === "admin";

	if (!isAdmin) {
		return (
			<div className="p-8 text-center">
				<h2 className="text-2xl font-bold mb-4">Access Denied</h2>
				<p>You do not have permission to view this page.</p>
				<button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => navigate("/")}>Go Home</button>
			</div>
		);
	}

	const refresh = async () => {
		try {
			const res = await fetchContactStaff();
			setItems(res || []);
		} catch (err) {
			console.error(err);
			setError("Failed to refresh contact staff items.");
		}
	};

	const handleAdd = () => {
		setDialogInitial(null);
		setDialogOpen(true);
	};

	const handleEdit = (item) => {
		setDialogInitial(item);
		setDialogOpen(true);
	};

	const handleSave = async (formData) => {
		setIsSaving(true);
		try {
			const isEditing = Boolean(dialogInitial && (dialogInitial._id || dialogInitial.id));
			if (isEditing) {
				await updateContactStaff(dialogInitial._id || dialogInitial.id, formData);
			} else {
				await createContactStaff(formData);
			}
			await refresh();
			showMessage(isEditing ? 'Contact staff member updated.' : 'Contact staff member added.', { type: 'success' });
			setDialogOpen(false);
			setDialogInitial(null);
		} catch (err) {
			console.error(err);
			showMessage('Failed to save contact staff item.', { type: 'error' });
		} finally {
			setIsSaving(false);
		}
	};

	const handleDeleteRequest = (item) => {
		setDeleteTarget({ id: item._id || item.id, name: item.name });
		setIsDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!deleteTarget?.id) return;
		setIsDeleting(true);
		try {
			await deleteContactStaff(deleteTarget.id);
			await refresh();
			showMessage('Contact staff member deleted.', { type: 'success' });
			setIsDeleteDialogOpen(false);
			setDeleteTarget(null);
		} catch (err) {
			console.error(err);
			showMessage('Failed to delete contact staff item.', { type: 'error' });
		} finally {
			setIsDeleting(false);
		}
	};

	const handleDeleteCancel = () => {
		if (isDeleting) return;
		setIsDeleteDialogOpen(false);
		setDeleteTarget(null);
	};

	const formatRegionNames = (value) => {
		if (!value) return '';
		const ids = Array.isArray(value)
			? value
			: typeof value === 'string'
				? value.split(',').map((part) => part.trim()).filter(Boolean)
				: [value];
		const names = ids.map((id) => regionsLookup[id] || id).filter(Boolean);
		return names.join(', ');
	};

	return (
		<div className="p-6 max-w-4xl mx-auto">
			<h1 className="text-3xl font-bold mb-4">Admin — Contact Staff</h1>

			<div className="mb-4">
				<button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded">Add Contact Staff</button>
			</div>

			{loading ? (
				<p className="text-gray-600">Loading contact staff...</p>
			) : error ? (
				<p className="text-red-600">{error}</p>
			) : (
				<div className="space-y-4">
					{items.map((item) => (
						<div key={item._id || item.id} className="bg-white p-4 rounded shadow flex items-start gap-4">
							<div className="flex-1">
												<h3 className="text-xl font-semibold">{item.name} {item.title ? `— ${item.title}` : ''}</h3>
												{item.email && (
													<p className="mt-1 text-sm text-sky-600"><a href={`mailto:${item.email}`}>{item.email}</a></p>
												)}
												<p className="mt-2 text-gray-700">Role: {item.role}</p>
												<p className="mt-2 text-gray-700">Regions: {formatRegionNames(item.regions)}</p>
												<p className="mt-2 text-gray-700">{item.bio}</p>
								<div className="mt-3">
									<button onClick={() => handleEdit(item)} className="text-blue-600 mr-4">Edit</button>
									<button onClick={() => handleDeleteRequest(item)} className="text-red-600">Delete</button>
								</div>
							</div>
							{item.image && (
								<div className="w-24 h-24 rounded overflow-hidden">
									<img src={item.image} alt={item.name} className="w-full h-full object-cover" />
								</div>
							)}
						</div>
					))}
				</div>
			)}

			<AdminContactStaffDialogBox
				open={dialogOpen}
				initial={dialogInitial}
				onClose={() => {
					if (isSaving) return;
					setDialogOpen(false);
					setDialogInitial(null);
				}}
				onSave={handleSave}
				isSaving={isSaving}
			/>
			<AdminConfirmDialog
				open={isDeleteDialogOpen}
				title="Delete Contact Staff"
				message={deleteTarget?.name ? `Are you sure you want to delete ${deleteTarget.name}? This action cannot be undone.` : 'Are you sure you want to delete this contact staff member?'}
				confirmLabel="Delete"
				onConfirm={handleDeleteConfirm}
				onCancel={handleDeleteCancel}
				isProcessing={isDeleting}
			/>
		</div>
	);
};

export default AdminContactStaff;

